import Vue from 'vue';
import DetailedOutfitHelper from '@/services/DetailedOutfitHelper';
import MetricsHelper from '@/services/MetricsHelper';
import MixAndMatchHelper from '@/services/MixAndMatchHelper';
import FacialFeaturesHelper from '@/services/FacialFeaturesHelper';
import UndoRedoHelper from '@/services/UndoRedoHelper';
import SendClientMessage from '@/services/SendClientMessage';
import UrlHelper from '@/services/UrlHelper';
import { TraitOriginEnum } from '@/enum/trait_origin';
import { FlowModeEnum } from '@/enum/flow_mode';
import { OutfitTypeEnum } from '@/enum/outfit_type';
import { OutfitActionTypeEnum } from '@/enum/outfit_action_type';

import { OutfitSelectionTypeEnum } from '@/enum/outfit_selection_type';
import { ClosetStateEnum } from '@/enum/closet_state';
import {
  MixAndMatchCategoryEnum,
  MixAndMatchDefaultEnum,
  MixAndMatchDefaultTuckStateEnum,
  MixAndMatchTuckStateEnum,
  MixAndMatchUnderwearEnum,
} from '@/enum/mix_and_match';

import SixthBodyLegacyOptions from '@/services/SixthBodyLegacyOptions';
import SixthBodyHelper from '@/services/SixthBodyHelper';
import { UndoRedoActionEnum } from '@/enum/undo_redo';
import { OUTFIT_CATEGORY_KEY } from '@/constants';
import BadgingHelper from '@/services/BadgingHelper';
import CategoryAssets from '@/services/CategoryAssets';

import { MutationTree } from 'vuex';
import isEmpty from 'lodash/isEmpty';
import { snapchat } from '@/gen/proto/protos';
import IOutfitBrand = snapchat.bitmoji.api.IOutfitBrand;
import IIndividualOutfit = snapchat.bitmoji.api.IIndividualOutfit;
import TraitsHelper from '@/services/TraitsHelper';
import { GenderEnum } from '@/enum/gender';

const MANNEQUIN_PREVIEW_PATH_REGEX = /mannequin-(center|left|right)/;

const mutations: MutationTree<RootState> = {
  setUndoRedoButton(state, { isDisabled }) {
    state.disableUndoRedoButton = isDisabled;
  },
  loadAssetsData(state, assetsData) {
    state.categories = assetsData.categories;
    state.categoryMetadata = assetsData.metadata;
    state.mixAndMatchColourGroups = assetsData.mixAndMatchMetadata.color_group_swatch_sets;
    state.mixAndMatchMetadata = assetsData.mixAndMatchMetadata;
    state.facialFeaturesMetadata = assetsData.facialFeaturesMetadata;
  },
  loadInitialPromotions(state, promotionsData) {
    state.initialPromotions = promotionsData;
  },
  clearStaleCategoryPromotion(state, staleCategory) {
    // Need to assign new set instance to trigger state update. Set.add() alone does not do this
    state.stalePromotions.categories = new Set(state.stalePromotions.categories.add(staleCategory));

    // Also, clear all option promos under category
    const initialOptionPromos = state.initialPromotions.options.find((optionPromo) =>
      BadgingHelper.isNestedPromoForCategory(staleCategory, optionPromo),
    );
    const staleOptionPromosIndex = state.stalePromotions.options.findIndex((optionPromo) =>
      BadgingHelper.isNestedPromoForCategory(staleCategory, optionPromo),
    );
    if (initialOptionPromos) {
      const replaceExisting = staleOptionPromosIndex !== -1 ? 1 : 0;
      state.stalePromotions.options.splice(staleOptionPromosIndex, replaceExisting, {
        category: staleCategory,
        optionIds: initialOptionPromos.optionIds,
      });
    }
  },
  clearStaleOptionPromotion(state, { category, optionId }) {
    const staleOptionPromosIndex = state.stalePromotions.options.findIndex((optionPromos) =>
      BadgingHelper.isNestedPromoForCategory(category, optionPromos),
    );
    let staleOptionsLength;
    if (staleOptionPromosIndex === -1) {
      state.stalePromotions.options.push({ category: category, optionIds: [optionId] });
      staleOptionsLength = 1;
    } else {
      staleOptionsLength = state.stalePromotions.options[staleOptionPromosIndex].optionIds.push(
        optionId,
      );
    }

    // Also immediately clear category promotion if all of it's options are stale
    const initialOptionPromos = state.initialPromotions.options.find((optionPromos) =>
      BadgingHelper.isNestedPromoForCategory(category, optionPromos),
    );
    const initialOptionsLength = initialOptionPromos?.optionIds?.length;
    if (staleOptionsLength === initialOptionsLength) {
      state.stalePromotions.categories = new Set(state.stalePromotions.categories.add(category));
    }
  },
  mergeDefaultsWithInitialTraits(state, defaults) {
    // bitmoji api preview will default to regular outfit if "outfit" param is sent, so remove it if the user is
    // wearing a split outfit. Defaults automatically has the default regular outfit set.
    if (state.initialTraits.clothing_type === OutfitTypeEnum.SPLIT) {
      delete defaults.outfit;
      delete state.initialTraits.outfit;
    }

    Object.keys(defaults).forEach((key) => {
      Vue.set(state.traitOrigins, key, TraitOriginEnum.DEFAULT);
    });

    Object.keys(state.initialTraits).forEach((key) => {
      if (state.flowMode === FlowModeEnum.CREATE) {
        // all params passed in at CREATE flow are generated by Mirror prediction
        Vue.set(state.traitOrigins, key, TraitOriginEnum.MIRROR);
      } else {
        Vue.set(state.traitOrigins, key, TraitOriginEnum.UNKNOWN);
      }
    });

    if (state.flowMode === FlowModeEnum.CREATE) {
      // Only set the defaults for the create mode. Edit and fashion flows will have the users selected traits
      // set from their previous avatar. If we set defaults in create it will set face_proportion which
      // overrides some user's face shapes for bitstrips style (if they created using sliders).
      // https://jira.sc-corp.net/browse/BM-9943

      state.selectedTraits = Object.assign({}, defaults, state.initialTraits);
    } else {
      state.selectedTraits = Object.assign({}, state.initialTraits);
    }
  },
  setCategorySelected(state, { index }) {
    // Only load the closet data the first time the user opens the closet in a web builder session.
    if (
      state.categories &&
      state.categories[index] &&
      state.categories[index].key === 'my_closet' &&
      !state.closetItems &&
      state.closetState === ClosetStateEnum.NOT_LOADING
    ) {
      if (state.flowMode === FlowModeEnum.CREATE) {
        // Users in the create flow will not have closet items
        state.closetItems = [];
        state.closetState === ClosetStateEnum.LOADED;
      } else {
        const isClosetSupported = SendClientMessage.loadCloset();
        state.closetState = isClosetSupported
          ? ClosetStateEnum.LOADING
          : ClosetStateEnum.DO_NOT_SUPPORT;
      }
    }
    state.selectedCategoryIndex = index;
  },
  setNewCategoryIndex(state, { index }) {
    state.newCategoryIndex = index;
  },
  setSelectedCategoryIndexToNewCategoryIndex(state) {
    state.selectedCategoryIndex = state.newCategoryIndex;
  },
  setInitialSectionIdToDefault(state) {
    state.initialSectionId = null;
  },
  categoryIconCentered(state) {
    state.centerActiveCategoryIcon = false;
  },
  setOutfit(state, { type, value }) {
    // setOutfit is called when a user selects a full outfit.
    // A full outfit can be either a regular or detailed(split) outfit.
    const valuesToSet = DetailedOutfitHelper.getOutfitValuesToSet(type, value);
    const valuesToDelete = DetailedOutfitHelper.keysToDelete();

    valuesToDelete.forEach((key) => {
      Vue.delete(state.selectedTraits, key);
    });
    Object.keys(valuesToSet).forEach((key) => {
      Vue.set(state.selectedTraits, key, valuesToSet[key]);
    });

    // If resulting traits have top or bottom as 0 then replace with underwear.
    if (
      state.initialTraits.gender === GenderEnum.FEMALE &&
      state.selectedTraits[MixAndMatchCategoryEnum.TOP] === 0
    ) {
      Vue.set(
        state.selectedTraits,
        MixAndMatchCategoryEnum.TOP,
        MixAndMatchHelper.underwearIdForCategory(MixAndMatchCategoryEnum.TOP),
      );
      const palette = MixAndMatchHelper.underwearColourPaletteForCategory(
        MixAndMatchCategoryEnum.TOP,
      );
      Object.keys(palette).forEach((colourToneKey) => {
        Vue.set(state.selectedTraits, colourToneKey, palette[colourToneKey]);
      });
    }
    if (state.selectedTraits[MixAndMatchCategoryEnum.BOTTOM] === 0) {
      Vue.set(
        state.selectedTraits,
        MixAndMatchCategoryEnum.BOTTOM,
        MixAndMatchHelper.underwearIdForCategory(MixAndMatchCategoryEnum.BOTTOM),
      );
      const palette = MixAndMatchHelper.underwearColourPaletteForCategory(
        MixAndMatchCategoryEnum.BOTTOM,
      );
      Object.keys(palette).forEach((colourToneKey) => {
        Vue.set(state.selectedTraits, colourToneKey, palette[colourToneKey]);
      });
    }

    state.outfitSelectionType = OutfitSelectionTypeEnum.FULL;
    MetricsHelper.logOutfitAction(
      OutfitActionTypeEnum.TAP_FULL_OUTFIT,
      state.selectedTraits,
      state.initialTraits.gender,
      state.initialTraits.style,
      state.selectedCategoryIndex,
      state.flowMode,
    );
  },
  setMixAndMatchDefaultOutfit(state) {
    if (state.flowMode !== FlowModeEnum.CREATE) {
      return;
    }

    const setPalette = (palette: Record<string, number>) => {
      Object.keys(palette).forEach((colourToneKey) => {
        Vue.set(state.selectedTraits, colourToneKey, palette[colourToneKey]);
        Vue.set(state.traitOrigins, colourToneKey, TraitOriginEnum.DEFAULT);
      });
    };

    Vue.set(state.initialTraits, MixAndMatchCategoryEnum.TOP, MixAndMatchDefaultEnum.TOP);
    Vue.set(state.selectedTraits, MixAndMatchCategoryEnum.TOP, MixAndMatchDefaultEnum.TOP);
    Vue.set(state.traitOrigins, MixAndMatchCategoryEnum.TOP, TraitOriginEnum.DEFAULT);
    setPalette(MixAndMatchHelper.TOP_UNDERWEAR_COLOUR_PALETTE);

    Vue.set(state.initialTraits, MixAndMatchCategoryEnum.BOTTOM, MixAndMatchDefaultEnum.BOTTOM);
    Vue.set(state.selectedTraits, MixAndMatchCategoryEnum.BOTTOM, MixAndMatchDefaultEnum.BOTTOM);
    Vue.set(state.traitOrigins, MixAndMatchCategoryEnum.BOTTOM, TraitOriginEnum.DEFAULT);
    setPalette(MixAndMatchHelper.BOTTOM_UNDERWEAR_COLOUR_PALETTE);

    Vue.set(state.initialTraits, MixAndMatchCategoryEnum.FOOTWEAR, MixAndMatchDefaultEnum.FOOTWEAR);
    Vue.set(
      state.selectedTraits,
      MixAndMatchCategoryEnum.FOOTWEAR,
      MixAndMatchDefaultEnum.FOOTWEAR,
    );
    Vue.set(state.traitOrigins, MixAndMatchCategoryEnum.FOOTWEAR, TraitOriginEnum.DEFAULT);

    Vue.set(state.initialTraits, 'clothing_type', OutfitTypeEnum.SPLIT);
    Vue.set(state.selectedTraits, 'clothing_type', OutfitTypeEnum.SPLIT);
    Vue.set(state.traitOrigins, 'clothing_type', TraitOriginEnum.DEFAULT);

    Vue.set(state.initialTraits, DetailedOutfitHelper.IS_TUCKED_KEY, 0);
    Vue.set(state.selectedTraits, DetailedOutfitHelper.IS_TUCKED_KEY, 0);
    Vue.set(state.traitOrigins, DetailedOutfitHelper.IS_TUCKED_KEY, TraitOriginEnum.DEFAULT);

    Vue.delete(state.initialTraits, OUTFIT_CATEGORY_KEY);
    Vue.delete(state.selectedTraits, OUTFIT_CATEGORY_KEY);
    Vue.delete(state.traitOrigins, OUTFIT_CATEGORY_KEY);
  },
  setMixAndMatchItem(state, { category, value }) {
    // setMixAndMatchItem is called when a user selects a garment or removes a garment
    // from one of the mix and match categories (top, bottomm, footwear, socks, outerwear).
    state.hasTappedMixAndMatchItemInSession = true;

    // If the user selects a mix and match garment while wearing an original outfit, delete all of the detailed outfit
    // keys that may be leftover from the initial traits.
    // If the user selects a mix and match garment while wearing a detailed outfit, we delete the colour tones for the
    // category in which the item was selected. Otherwise, unused colour tones may persist in the selected traits.
    const valuesToDelete = MixAndMatchHelper.keysToDeleteForMixAndMatchAction(
      category,
      state.selectedTraits.clothing_type,
    );
    valuesToDelete.forEach((key) => {
      if (key in state.selectedTraits) {
        Vue.delete(state.selectedTraits, key);
      }
    });

    // The valuesToSet is the union of the selected item keys and the VITAL_DETAILED_OUTFIT_KEYS.
    // All of the VITAL_DETAILED_OUTFIT_KEYS need a value or the avatar will render with missing body parts.
    const valuesToSet = Object.keys(value);

    for (const key of DetailedOutfitHelper.VITAL_DETAILED_OUTFIT_KEYS) {
      if (!valuesToSet.includes(key)) {
        valuesToSet.push(key);
      }
    }
    for (const key of valuesToSet) {
      if (key in value && (value[key] !== 0 || key.includes('tone'))) {
        // The user selected an item in this category.
        Vue.set(state.selectedTraits, key, value[key]);
      } else if (
        !(key in state.selectedTraits) ||
        key in value ||
        state.selectedTraits.clothing_type === OutfitTypeEnum.ORIGINAL
      ) {
        // The user either selected the remove option in this category or has not selected an item for this category.
        const isUnderwearAvailableForCategory = MixAndMatchHelper.isUnderwearAvailableForCategory(
          key,
          state.initialTraits.gender,
        );
        if (!isUnderwearAvailableForCategory) {
          if (DetailedOutfitHelper.VITAL_DETAILED_OUTFIT_KEYS.includes(key)) {
            // For all vital categories except bottoms and tops for female, we show the body part as naked (value 0).
            Vue.set(state.selectedTraits, key, 0);
          } else {
            // For non-vital categories, we delete the key from the traits.
            Vue.delete(state.selectedTraits, key);
          }
        } else {
          Vue.set(state.selectedTraits, key, MixAndMatchHelper.underwearIdForCategory(key));
          const palette = MixAndMatchHelper.underwearColourPaletteForCategory(key);
          Object.keys(palette).forEach((colourToneKey) => {
            Vue.set(state.selectedTraits, colourToneKey, palette[colourToneKey]);
          });
        }
      }
    }
    Vue.set(state.selectedTraits, 'clothing_type', OutfitTypeEnum.SPLIT);

    state.outfitSelectionType = OutfitSelectionTypeEnum.MIX_AND_MATCH;
  },
  setColourPaletteForMixAndMatchItem(state, { category, colourPalette }) {
    const valuesToDelete = MixAndMatchHelper.keysToDeleteForMixAndMatchAction(
      category,
      state.selectedTraits.clothing_type,
    );
    // Delete the previously selected colour palette for this category from selectedTraits.

    valuesToDelete.forEach((key) => {
      if (key in state.selectedTraits) {
        Vue.delete(state.selectedTraits, key);
      }
    });

    // Set the new colour palette for this category in selectedTraits.
    Object.keys(colourPalette).forEach((key) => {
      Vue.set(state.selectedTraits, key, colourPalette[key]);
    });
    state.outfitSelectionType = OutfitSelectionTypeEnum.MIX_AND_MATCH;
    state.hasTappedMixAndMatchItemInSession = true;
  },
  // Add the new palette to the selectedColourPalettesForMixAndMatchItems index.
  // This allows the items selected palette to be persisted even when the user selects a different item.
  setSelectedColourPalettesForMixAndMatchItems(state, { category, itemId, colourPalette }) {
    if (!state.selectedColourPalettesForMixAndMatchItems[category]) {
      state.selectedColourPalettesForMixAndMatchItems[category] = {};
    }
    state.selectedColourPalettesForMixAndMatchItems[category][itemId] = colourPalette;
  },
  setAvatarToDefaultOutfit(state) {
    Vue.delete(state.selectedTraits, OUTFIT_CATEGORY_KEY);

    const defaultOutfitTraits = MixAndMatchHelper.getDefaultOutfitTraits();
    Object.keys(defaultOutfitTraits).forEach((traitKey) => {
      Vue.set(state.selectedTraits, traitKey, defaultOutfitTraits[traitKey]);
    });

    // Also have to delete other non vital keys for the case where an avatar has put on mix and match before
    DetailedOutfitHelper.DETAILED_OUTFIT_KEYS.forEach((outfitKey) => {
      if (!DetailedOutfitHelper.VITAL_DETAILED_OUTFIT_KEYS.includes(outfitKey)) {
        Vue.delete(state.selectedTraits, outfitKey);
      }
    });
  },
  logOutfitAction(state, { action }) {
    MetricsHelper.logOutfitAction(
      action,
      state.selectedTraits,
      state.initialTraits.gender,
      state.initialTraits.style,
      state.selectedCategoryIndex,
      state.flowMode,
    );
  },
  logTraitAction(state, { action }) {
    MetricsHelper.logTraitAction(
      action,
      state.selectedTraits,
      state.facialFeatureTraits,
      state.initialTraits.gender,
      state.initialTraits.style,
      state.selectedCategoryIndex,
      state.flowMode,
    );
  },
  logPageView(state) {
    MetricsHelper.logPageView(
      state.selectedCategoryIndex,
      state.initialTraits.gender,
      state.flowMode,
      state.initialTraits.style,
    );
  },
  setColourPalettesForSelectedOutfitPieces(state) {
    // If the user is wearing a mix and match compatible outfit, set the selected colour palettes of the individual pieces to match their outfit.
    if (state.selectedTraits.clothing_type === OutfitTypeEnum.SPLIT) {
      Object.keys(DetailedOutfitHelper.CATEGORY_CUSTOM_COLOUR_KEYS).forEach((category) => {
        const colourPalette = MixAndMatchHelper.colourPaletteForSelectedItemInCategory(
          category,
          state.selectedTraits,
        );
        const itemId = state.selectedTraits[category];
        if (Object.keys(colourPalette).length > 0) {
          if (!state.selectedColourPalettesForMixAndMatchItems[category]) {
            state.selectedColourPalettesForMixAndMatchItems[category] = {};
          }
          state.selectedColourPalettesForMixAndMatchItems[category][itemId] = colourPalette;
          // Bodysuit special case: if an avatar is wearing only the bodysuit top, also set the bottom colour of the bodysuit
          if (
            category === MixAndMatchCategoryEnum.TOP &&
            MixAndMatchHelper.getCurrentBodysuit(itemId, state.categories) &&
            !(
              state.selectedColourPalettesForMixAndMatchItems[MixAndMatchCategoryEnum.BOTTOM] &&
              state.selectedColourPalettesForMixAndMatchItems[MixAndMatchCategoryEnum.BOTTOM][
                itemId
              ]
            )
          ) {
            const colourPaletteWithBottomKeys = MixAndMatchHelper.replacePaletteTopWithBottomKey(
              colourPalette,
            );
            if (!state.selectedColourPalettesForMixAndMatchItems[MixAndMatchCategoryEnum.BOTTOM]) {
              state.selectedColourPalettesForMixAndMatchItems[MixAndMatchCategoryEnum.BOTTOM] = {};
            }
            state.selectedColourPalettesForMixAndMatchItems[MixAndMatchCategoryEnum.BOTTOM][
              itemId
            ] = colourPaletteWithBottomKeys;
          }
        }
      });
    }
  },
  setTuckForMixAndMatchTop(state, { tuckState, isTopTuckable }) {
    // Only set is_tucked if item is tuckable
    if (isTopTuckable) {
      Vue.set(state.selectedTraits, 'is_tucked', tuckState);
    } else {
      Vue.delete(state.selectedTraits, 'is_tucked');
    }
    // Maybe TODO(Laura): We may want to trigger an outfit action metric on tuck state change if we want to understand how
    // often users are using this feature.
    state.outfitSelectionType = OutfitSelectionTypeEnum.MIX_AND_MATCH;
    state.hasTappedMixAndMatchItemInSession = true;
  },
  setTrait(state, { category, value }) {
    Vue.set(state.selectedTraits, category, value);
    Vue.set(state.traitOrigins, category, TraitOriginEnum.USER);
  },
  setTraitColourOption(state, { category, colourOption }) {
    const colourKeyPrefix = TraitsHelper.getTraitColourKeyPrefix(category);
    const optionId = state.selectedTraits[category];

    // Clear any lingering colour keys for category up front (handles cases where number of tones may differ between options)
    Object.keys(state.selectedTraits).forEach((traitKey) => {
      if (traitKey.startsWith(colourKeyPrefix)) {
        Vue.delete(state.selectedTraits, traitKey);
      }
    });

    // Convert selected colour option to corresponding traitKey->colour mapping and save as selected traits
    const newColourSelections = TraitsHelper.mapColourOptionToSelectedTraits(
      category,
      colourOption,
    );
    Object.entries(newColourSelections).forEach(([key, colour]) => {
      Vue.set(state.selectedTraits, key, colour);
      Vue.set(state.traitOrigins, key, TraitOriginEnum.USER);
    });

    // Update colour option selection for this specific trait option so it is maintained if the user
    // re-selects this option
    if (!state.selectedColourOptionsForTraitItems[category]) {
      state.selectedColourOptionsForTraitItems[category] = {};
    }
    state.selectedColourOptionsForTraitItems[category][optionId] = colourOption;
  },
  setColourOptionsForSelectedTraitPieces(state) {
    // Save colour options specified in initial traits so these selections are preserved when
    // a user changes trait items
    TraitsHelper.COLOUR_PICKER_TRAIT_CATEGORIES.forEach((category) => {
      const optionId = state.selectedTraits[category];
      if (!optionId) {
        // nothing to do
        return;
      }

      const selectedColourPickerOption = TraitsHelper.getSelectedColourPickerOptionFromTraits(
        category,
        state.selectedTraits,
      );
      if (selectedColourPickerOption) {
        state.selectedColourOptionsForTraitItems[category] = {
          [optionId]: selectedColourPickerOption,
        };
      }
    });
  },
  setFacialFeature(state, { category, value, colour }) {
    Vue.set(state.traitOrigins, category, TraitOriginEnum.USER);

    const valuesToDelete = FacialFeaturesHelper.getCategoryTraitKeys(
      category,
      state.selectedTraits,
    );
    for (const trait of valuesToDelete) {
      Vue.delete(state.selectedTraits, trait);
      Vue.delete(state.traitOrigins, trait);
    }

    const newFacialFeatureTraits = FacialFeaturesHelper.getFacialFeatureTraits(
      category,
      value,
      colour,
    );
    for (const trait of Object.keys(newFacialFeatureTraits)) {
      Vue.delete(state.facialFeatureTraits, trait);
    }

    const valuesToSet = FacialFeaturesHelper.getFacialFeaturePreviewQueryParameters(
      state.facialFeaturesMetadata,
      category,
      String(value),
      String(colour),
    );

    Object.keys(valuesToSet).forEach((trait) => {
      Vue.set(state.selectedTraits, trait, valuesToSet[trait]);
      Vue.set(state.traitOrigins, trait, TraitOriginEnum.USER);
    });
    for (const trait of Object.keys(newFacialFeatureTraits)) {
      Vue.set(state.facialFeatureTraits, trait, newFacialFeatureTraits[trait]);
    }
  },
  setScrollerFunction(state, scrollerFn) {
    // TODO - Move to module
    state.scrollerFunction = scrollerFn;
  },
  setClosetItems(state, { closetItems }) {
    state.closetState = ClosetStateEnum.LOADED;
    state.closetItems = closetItems.map((item: ClosetItem) => ({
      ...item,
      image:
        item.image
          .replace(MANNEQUIN_PREVIEW_PATH_REGEX, 'mannequin-with-head-$1')
          .replace(/&skin_tone=\d+/, '')
          .replace('style=1', 'style=5') + '&ua=2',
    }));
    state.categories = state.categories.map(function(category) {
      if (category.key === 'my_closet') {
        category.trait_category_options = state.closetItems;
      }
      return category;
    });

    // filtered out outfits that do not support 6th body
    const filteredClosetItems: ClosetItem[] = [];
    closetItems.forEach((closetItem: ClosetItem, index: number) => {
      if (
        closetItem.clothing_type === OutfitTypeEnum.SPLIT ||
        !SixthBodyLegacyOptions.nonSixthBodyOutfits[closetItem.outfit_id]
      ) {
        let closetItemWithCorrectPreview = Object.assign({}, closetItem);
        closetItemWithCorrectPreview.image = SixthBodyHelper.replacePreviewUrlWithCorrectRotation(
          closetItem.image,
          filteredClosetItems.length,
        );
        filteredClosetItems.push(closetItemWithCorrectPreview);
      }
    });
    state.closetItemsSixthBody = filteredClosetItems;
  },
  setClosetState(state, { closetState }) {
    state.closetState = closetState;
  },
  updateAvatarActions(state, { initialTraits, initialBackground, facialFeatureTraits }) {
    // Get differences between initial traits and current traits
    // where initial traits were the avatar traits before this action was performed
    const traitDifferences = UndoRedoHelper.getTraitsDifferences(
      initialTraits,
      state.selectedTraits,
    );
    if (
      Object.keys(traitDifferences.from).length === Object.keys(traitDifferences.to).length &&
      Object.keys(traitDifferences.to).length === 0
    ) {
      // This is definitely not an important action. This is because there are no keys in from/to for undo/redo to loop through
      return;
    }

    const diferrences: AvatarAction = { traits: traitDifferences };
    if (initialBackground) {
      const currentBackground = {
        backgroundId: state.backgroundId,
        backgroundUrl: state.backgroundUrl,
        backgroundType: state.backgroundType,
      };
      diferrences.background = { from: initialBackground, to: currentBackground };
    }
    if (facialFeatureTraits) {
      const facialFeatureTraitsDifferences = UndoRedoHelper.getFacialFeatureTraitsDifferences(
        facialFeatureTraits,
        state.facialFeatureTraits,
      );
      diferrences.facialFeatureTraits = facialFeatureTraitsDifferences;
    }

    // Indexing the actions starts at -1
    const indexToAddToAction = state.avatarActionIndex === -1 ? 0 : state.avatarActionIndex;
    state.avatarActions = state.avatarActions.slice(indexToAddToAction);
    state.avatarActions.unshift(diferrences);
    state.avatarActionIndex = -1;
    state.previousUndoRedoAction = null;
  },
  setAvatarActionIndex(state, { newAvatarActionIndex }) {
    state.avatarActionIndex = newAvatarActionIndex;
  },
  undoAvatarAction(state) {
    // If the previous action was not an "undo", we need to count up the action index before getting the traits
    // so that the action to be applied will be at the correct position
    if (state.previousUndoRedoAction !== UndoRedoActionEnum.UNDO) {
      state.avatarActionIndex = state.avatarActionIndex + 1;
    }
    const currentAvatarActions = state.avatarActions[state.avatarActionIndex];
    if (currentAvatarActions && currentAvatarActions.traits && currentAvatarActions.traits.from) {
      const traitsToApply = currentAvatarActions.traits.from;
      Object.keys(traitsToApply).forEach((key) => {
        if (traitsToApply[key] || traitsToApply[key] === 0) {
          Vue.set(state.selectedTraits, key, traitsToApply[key]);
          // Set the tuck toggle to the correct position
          if (key === DetailedOutfitHelper.IS_TUCKED_KEY) {
            state.topToggleTuckState = traitsToApply[key];
            state.isTopToggleDisabled = false;
          }
        } else {
          Vue.delete(state.selectedTraits, key);

          // If is_tucked is null, then disable the tuck toggle
          if (key === DetailedOutfitHelper.IS_TUCKED_KEY) {
            state.isTopToggleDisabled = true;
          }
        }
      });
    }
    // handle background change
    if (
      currentAvatarActions &&
      currentAvatarActions.background &&
      currentAvatarActions.background.from
    ) {
      const backgroundToApply = currentAvatarActions.background.from;
      state.backgroundId = backgroundToApply.backgroundId;
      state.backgroundUrl = backgroundToApply.backgroundUrl;
      state.backgroundType = backgroundToApply.backgroundType;
    }
    if (
      currentAvatarActions &&
      currentAvatarActions.facialFeatureTraits &&
      currentAvatarActions.facialFeatureTraits.from
    ) {
      const traitsToApply = currentAvatarActions.facialFeatureTraits.from;
      Object.keys(traitsToApply).forEach((key) => {
        if (traitsToApply[key]) {
          Vue.set(state.facialFeatureTraits, key, traitsToApply[key]);
        } else {
          Vue.delete(state.facialFeatureTraits, key);
        }
      });
    }

    state.avatarActionIndex = state.avatarActionIndex + 1;
    state.previousUndoRedoAction = UndoRedoActionEnum.UNDO;
  },
  redoAvatarAction(state) {
    // If the previous action was not a "redo", we need to count down the action index before getting the traits
    // so that the action to be applied will be at the correct position
    if (state.previousUndoRedoAction !== UndoRedoActionEnum.REDO) {
      state.avatarActionIndex = state.avatarActionIndex - 1;
    }
    const currentAvatarActions = state.avatarActions[state.avatarActionIndex];
    if (currentAvatarActions && currentAvatarActions.traits && currentAvatarActions.traits.to) {
      const traitsToApply = currentAvatarActions.traits.to;

      Object.keys(traitsToApply).forEach((key) => {
        if (traitsToApply[key] || traitsToApply[key] === 0) {
          Vue.set(state.selectedTraits, key, traitsToApply[key]);

          // Set the tuck toggle to the correct position
          if (key === DetailedOutfitHelper.IS_TUCKED_KEY) {
            state.topToggleTuckState = traitsToApply[key];
            state.isTopToggleDisabled = false;
          }
        } else {
          Vue.delete(state.selectedTraits, key);

          // If is_tucked is null, then disable the tuck toggle
          if (key === DetailedOutfitHelper.IS_TUCKED_KEY) {
            state.isTopToggleDisabled = true;
          }
        }
      });
    }
    // handle background change
    if (
      currentAvatarActions &&
      currentAvatarActions.background &&
      currentAvatarActions.background.to
    ) {
      const backgroundToApply = currentAvatarActions.background.to;
      state.backgroundId = backgroundToApply.backgroundId;
      state.backgroundUrl = backgroundToApply.backgroundUrl;
      state.backgroundType = backgroundToApply.backgroundType;
    }

    if (
      currentAvatarActions &&
      currentAvatarActions.facialFeatureTraits &&
      currentAvatarActions.facialFeatureTraits.to
    ) {
      const traitsToApply = currentAvatarActions.facialFeatureTraits.to;
      Object.keys(traitsToApply).forEach((key) => {
        if (traitsToApply[key]) {
          Vue.set(state.facialFeatureTraits, key, traitsToApply[key]);
        } else {
          Vue.delete(state.facialFeatureTraits, key);
        }
      });
    }

    state.avatarActionIndex = state.avatarActionIndex - 1;
    state.previousUndoRedoAction = UndoRedoActionEnum.REDO;
  },
  scrollToPage(state, { index }) {
    if (state?.scrollerFunction) {
      state.scrollerFunction(index);
    }
  },
  scrollToPageWithCategoryKey(state, { categoryKey }) {
    const categoryIndex = CategoryAssets.getCategoryIndex(
      categoryKey,
      state.initialTraits.gender,
      state.initialTraits.style,
      state.flowMode,
    );
    // Don't try to scroll to a category that is the current category and
    // break the scrolling behaviour:
    // - https://sentry.sc-corp.net/sentry/web-avatar-builder/issues/5302786/
    if (state.selectedCategoryIndex === categoryIndex) {
      return;
    }
    state.newCategoryIndex = categoryIndex;
    if (state?.scrollerFunction) {
      state.scrollerFunction(categoryIndex);
    }
  },
  scrollState(state, scrollState) {
    state.scrollState = scrollState;
  },
  setIsFullBodyCategory(state, { isFullBodyCategory }) {
    state.isFullBodyCategory = isFullBodyCategory;
  },
  setTopToggleTuckState(state, { tuckState }) {
    state.topToggleTuckState = tuckState;
  },
  setIsTopToggleDisabled(state, { isDisabled }) {
    state.isTopToggleDisabled = isDisabled;
  },
  tuckControlSetForCurrentOutfit(state) {
    if (!isEmpty(state.selectedTraits) && state.mixAndMatchMetadata?.tops_default_tuck_state) {
      const topDefaultTuckState =
        state.mixAndMatchMetadata.tops_default_tuck_state[state.selectedTraits.top] &&
        state.mixAndMatchMetadata.tops_default_tuck_state[state.selectedTraits.top].tuck_state[
          state.initialTraits.gender
        ];
      const isWearingUnderwear =
        state.selectedTraits.top === 0 ||
        state.selectedTraits.top === MixAndMatchUnderwearEnum.UNDERWEAR_TOP ||
        state.selectedTraits.bottom === MixAndMatchUnderwearEnum.UNDERWEAR_BOTTOM;
      const topIsTuckable =
        topDefaultTuckState && topDefaultTuckState !== MixAndMatchDefaultTuckStateEnum.NONE;
      if (state.mixAndMatchMetadata?.bottoms_tuckable) {
        const bottomIsTuckable =
          state.mixAndMatchMetadata.bottoms_tuckable[state.selectedTraits.bottom];

        state.topToggleTuckState = state.selectedTraits.is_tucked
          ? MixAndMatchTuckStateEnum.TUCK
          : MixAndMatchTuckStateEnum.UNTUCK;
        Vue.set(state.tuckStateForTop, state.selectedTraits.top, state.topToggleTuckState);
        state.isTopToggleDisabled =
          state.selectedTraits.clothing_type !== OutfitTypeEnum.SPLIT ||
          isWearingUnderwear ||
          !topIsTuckable ||
          !bottomIsTuckable;
      }
    }
  },
  filteredSixthBodyOutfits(state) {
    const sixthBodyBrands: IOutfitBrand[] = [];
    state.categories.forEach((category) => {
      if (category.key === OUTFIT_CATEGORY_KEY && category.options) {
        const categoryOptions = <IOutfitBrand[]>category.options;
        categoryOptions.forEach((outfitBrand) => {
          const sixthBodySupportedOutfits: IIndividualOutfit[] = [];
          if (outfitBrand.outfits) {
            outfitBrand.outfits.forEach((outfit, index) => {
              if (
                (outfit.id && !SixthBodyLegacyOptions.nonSixthBodyOutfits[outfit.id]) ||
                outfit.outfit_type === OutfitTypeEnum.SPLIT
              ) {
                let outfitWithCorrectRender = outfit;

                // Only create new render urls if an outfit has been filtered out (if the index doesn't match the length)
                // Some brands (e.g. NBA), entirely support sixth body, so we don't need to change rendering url
                if (
                  outfit.id &&
                  index !== sixthBodySupportedOutfits.length &&
                  outfit.outfit_type !== OutfitTypeEnum.SPLIT
                ) {
                  outfitWithCorrectRender.image = UrlHelper.getLegacyOutfitPreview(
                    outfit.id,
                    sixthBodySupportedOutfits.length,
                    state.initialTraits.gender,
                  );
                } else if (outfit.image) {
                  // We no longer support style=1 mannequin renders
                  outfitWithCorrectRender.image =
                    outfit.image
                      .replace(MANNEQUIN_PREVIEW_PATH_REGEX, 'mannequin-with-head-$1')
                      .replace(/&skin_tone=\d+/, '')
                      .replace('style=1', 'style=5') + '&cacheable=1&ua=2';
                }
                sixthBodySupportedOutfits.push(outfitWithCorrectRender);
              }
            });

            // Some brand doesn't support sixth body at all, check for those
            if (sixthBodySupportedOutfits.length > 0) {
              const newOutfitBrand = Object.assign({}, outfitBrand);
              newOutfitBrand.outfits = sixthBodySupportedOutfits;
              sixthBodyBrands.push(newOutfitBrand);
            }
          }
        });
      }
    });
    state.sixthBodyLegacyOutfits = sixthBodyBrands;
  },
  checkForIOS12(state) {
    const userAgent = window.navigator.userAgent;
    const osIndex = userAgent.indexOf('OS ');
    if ((userAgent.indexOf('iPhone') > -1 || userAgent.indexOf('iPad') > -1) && osIndex > -1) {
      const substringStart = osIndex + 3;
      const substringEnd = osIndex + 6;
      const iOSVersion = parseInt(userAgent.substring(substringStart, substringEnd));
      if (iOSVersion === 12) {
        state.isIOS12 = true;
      }
    }
  },
  setShouldShowDeleteSelfieDialog(state, { show }) {
    state.shouldShowDeleteSelfieDialog = show;
  },
  setShouldShowExitDialogIfChanges(state, { show }) {
    if (show) {
      state.shouldShowExitDialog =
        JSON.stringify(state.initialTraits) !== JSON.stringify(state.selectedTraits) ||
        state.forceShowExitDialog;
    } else {
      state.shouldShowExitDialog = show;
    }
  },
  setExitDialogType(state, { type }) {
    state.exitDialogType = type;
  },
  setShouldShowOutfitDialog(state, { show }) {
    state.shouldShowOutfitDialog = show;
  },
  setShouldShowUnsupportedOSError(state, { show }) {
    state.setShouldShowUnsupportedOSError = show;
  },
  setShouldShowItemUnavailableDialog(state, { show }) {
    state.shouldShowItemUnavailableDialog = show;
  },
  setBackgroundId(state, { id }) {
    state.selectedBackgroundIds.add(id);
    state.backgroundId = id;
  },
  setBackgroundType(state, { type }) {
    state.backgroundType = type;
  },
  setBackgroundUrl(state, { url }) {
    state.backgroundUrl = url;
  },
  setIsSelfiePreviewDismissed(state, { isDismissed }) {
    state.isSelfiePreviewDismissed = isDismissed;
  },
  setIsSelfiePreviewExpanded(state, { isExpanded }) {
    state.isSelfiePreviewExpanded = isExpanded;
  },
  setIsSavingAvatar(state, { isSavingAvatar }) {
    state.isSavingAvatar = isSavingAvatar;
  },
  setTuckStateForTop(state, { tuckState, value }) {
    Vue.set(state.tuckStateForTop, value, tuckState);
  },
  setBottomWrapDown(state, { bottomWrapDown }) {
    state.bottomWrapDown = bottomWrapDown;
  },
  setBuilderIsFocused(state, { isFocused }) {
    state.builderIsFocused = isFocused;
  },
  setOutfitSharingState(state, { outfitSharingState }) {
    state.outfitSharingState = outfitSharingState;
  },
};

export default mutations;
